import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import Illustration from '../../images/login/illustration.png';
import BgPattern from '../../images/login/bg-pattern.png';

// ===== 전체 컨테이너 =====
const Page = styled.div`
  min-height: 100vh;
  background:
    linear-gradient(180deg, rgba(245, 245, 250, 0.6) 0%, rgba(238, 242, 255, 0.6) 100%),
    url(${BgPattern}) center/cover no-repeat fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  box-sizing: border-box;
  transition: ${p => p.theme.transition};
`;

const Card = styled.div`
  width: 100%;
  max-width: 880px;
  background: ${p => p.theme.bgCard};
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(79, 70, 229, 0.12);
  border: 1px solid ${p => p.theme.border};
  display: grid;
  grid-template-columns: 1fr 1fr;
  overflow: hidden;

  @media screen and (max-width: 760px) {
    grid-template-columns: 1fr;
    max-width: 420px;
  }
`;

// ===== 좌측 브랜딩 패널 (일러스트로 가득 채움) =====
const BrandingPanel = styled.div`
  background: #f2f0fd;
  padding: 28px 24px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: 760px) {
    display: none;
  }
`;

const BrandIllustration = styled.img`
  width: 100%;
  height: auto;
  max-height: 100%;
  display: block;
  object-fit: contain;
  object-position: center;
`;

// ===== 우측 폼 패널 =====
const FormPanel = styled.div`
  padding: 36px 32px 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: ${p => p.theme.bgCard};

  @media screen and (max-width: 760px) {
    padding: 32px 24px;
  }
`;

const FormTitle = styled.h1`
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 800;
  color: ${p => p.theme.text};
`;

const FormSub = styled.p`
  margin: 0 0 28px;
  font-size: 13px;
  color: ${p => p.theme.textMuted};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const Label = styled.label`
  font-weight: 700;
  color: ${p => p.theme.text};
  font-size: 13px;
`;

const InlineLink = styled.span`
  font-size: 12px;
  color: ${p => p.theme.accent};
  cursor: pointer;
  font-weight: 600;

  &:hover { text-decoration: underline; }
`;

const Input = styled.input`
  padding: 12px 14px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: 10px;
  font-size: 14px;
  background: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.accent};
  }

  &::placeholder {
    color: ${p => p.theme.textMuted};
  }
`;

const PrimaryButton = styled.button`
  margin-top: 8px;
  padding: 13px;
  background: ${p => p.theme.accent};
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: ${p => p.theme.transition};

  &:hover { background: ${p => p.theme.accentHover}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const FooterLine = styled.div`
  margin-top: 26px;
  text-align: center;
  font-size: 13px;
  color: ${p => p.theme.textMuted};

  a, span {
    color: ${p => p.theme.accent};
    text-decoration: none;
    font-weight: 600;
    cursor: pointer;
    margin-left: 6px;

    &:hover { text-decoration: underline; }
  }
`;

const Message = styled.div`
  padding: 11px 14px;
  border-radius: 10px;
  font-size: 13px;
  margin-bottom: 14px;

  &.error {
    background: #FEE2E2;
    color: #DC2626;
    border: 1px solid #FECACA;
  }
  &.success {
    background: #D1FAE5;
    color: #059669;
    border: 1px solid #A7F3D0;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
`;

const StepDot = styled.div`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: ${p => p.$active ? p.theme.accent : p.theme.border};
  transition: background 0.2s ease;
`;

const Footer = styled.div`
  margin-top: 16px;
  font-size: 11px;
  color: ${p => p.theme.textMuted};
`;

const Login = () => {
  const [isRegister, setIsRegister] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [forgotAccount, setForgotAccount] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [resetToken, setResetToken] = useState('');

  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId, user_pwd: password })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        login(data.user);
      } else {
        setError(data.error || '로그인에 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (password !== passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (password.length < 4) {
      setError('비밀번호는 4자 이상이어야 합니다.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_id: userId,
          user_pwd: password,
          user_name: userName,
          user_email: email
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess('회원가입이 완료되었습니다. 로그인해주세요.');
        setIsRegister(false);
        setPassword('');
        setPasswordConfirm('');
        setUserName('');
        setEmail('');
      } else {
        setError(data.error || '회원가입에 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotSendCode = async (e) => {
    e.preventDefault();
    if (!forgotAccount) {
      setError('계정을 입력해주세요.');
      return;
    }
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/api/auth/forgot-password/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_account: forgotAccount })
      });
      const data = await response.json();
      if (data.success) {
        setForgotStep(2);
        setSuccess('인증번호가 전송되었습니다.');
      } else {
        setError(data.message || '전송에 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotVerify = async (e) => {
    e.preventDefault();
    if (!verificationCode) {
      setError('인증번호를 입력해주세요.');
      return;
    }
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/api/auth/forgot-password/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_account: forgotAccount, code: verificationCode })
      });
      const data = await response.json();
      if (data.success) {
        setResetToken(data.reset_token);
        setForgotStep(3);
        setSuccess('인증 성공! 새 비밀번호를 입력해주세요.');
      } else {
        setError(data.message || '인증번호가 올바르지 않습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotReset = async (e) => {
    e.preventDefault();
    if (!newPassword || !newPasswordConfirm) {
      setError('비밀번호를 입력해주세요.');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (newPassword.length < 4) {
      setError('비밀번호는 4자 이상이어야 합니다.');
      return;
    }
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/api/auth/forgot-password/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset_token: resetToken, new_password: newPassword })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('비밀번호가 변경되었습니다.');
        setTimeout(() => {
          setIsForgotPassword(false);
          setForgotStep(1);
          setForgotAccount('');
          setVerificationCode('');
          setNewPassword('');
          setNewPasswordConfirm('');
          setResetToken('');
          setSuccess('');
        }, 2000);
      } else {
        setError(data.message || '비밀번호 변경에 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 우측 폼 본문
  const renderRightForm = () => {
    if (isForgotPassword) {
      return (
        <>
          <FormTitle>비밀번호 찾기</FormTitle>
          <FormSub>
            {forgotStep === 1 && '계정을 입력하면 인증번호가 전송됩니다.'}
            {forgotStep === 2 && '전송된 인증번호를 입력해주세요.'}
            {forgotStep === 3 && '새 비밀번호를 설정해주세요.'}
          </FormSub>
          <StepIndicator>
            <StepDot $active={forgotStep >= 1} />
            <StepDot $active={forgotStep >= 2} />
            <StepDot $active={forgotStep >= 3} />
          </StepIndicator>

          {error && <Message className="error">{error}</Message>}
          {success && <Message className="success">{success}</Message>}

          <Form onSubmit={
            forgotStep === 1 ? handleForgotSendCode :
            forgotStep === 2 ? handleForgotVerify : handleForgotReset
          }>
            {forgotStep === 1 && (
              <FormGroup>
                <FieldLabelRow><Label>계정 (아이디)</Label></FieldLabelRow>
                <Input
                  type="text"
                  value={forgotAccount}
                  onChange={(e) => setForgotAccount(e.target.value)}
                  placeholder="아이디를 입력하세요"
                  required
                />
              </FormGroup>
            )}

            {forgotStep === 2 && (
              <FormGroup>
                <FieldLabelRow><Label>인증번호</Label></FieldLabelRow>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="인증번호 입력"
                  required
                />
              </FormGroup>
            )}

            {forgotStep === 3 && (
              <>
                <FormGroup>
                  <FieldLabelRow><Label>새 비밀번호</Label></FieldLabelRow>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="새 비밀번호"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <FieldLabelRow><Label>비밀번호 확인</Label></FieldLabelRow>
                  <Input
                    type="password"
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    placeholder="비밀번호 확인"
                    required
                  />
                </FormGroup>
              </>
            )}

            <PrimaryButton type="submit" disabled={isLoading}>
              {isLoading ? '처리중...' :
                forgotStep === 1 ? '인증번호 전송' :
                forgotStep === 2 ? '인증번호 확인' : '비밀번호 변경'}
            </PrimaryButton>
          </Form>

          <FooterLine>
            <span onClick={() => {
              setIsForgotPassword(false);
              setForgotStep(1);
              setError('');
              setSuccess('');
            }}>로그인으로 돌아가기</span>
          </FooterLine>
        </>
      );
    }

    return (
      <>
        <FormTitle>{isRegister ? '회원가입' : '로그인'}</FormTitle>
        <FormSub>
          {isRegister
            ? '계정 정보를 입력해주세요.'
            : '원하시는 ID로 견적서를 빠르고 정확하게 작성하세요.'}
        </FormSub>

        {error && <Message className="error">{error}</Message>}
        {success && <Message className="success">{success}</Message>}

        <Form onSubmit={isRegister ? handleRegister : handleLogin}>
          <FormGroup>
            <FieldLabelRow><Label>아이디</Label></FieldLabelRow>
            <Input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="아이디를 입력하세요"
              required
            />
          </FormGroup>

          {isRegister && (
            <>
              <FormGroup>
                <FieldLabelRow><Label>이름</Label></FieldLabelRow>
                <Input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="이름을 입력하세요"
                />
              </FormGroup>
              <FormGroup>
                <FieldLabelRow><Label>이메일</Label></FieldLabelRow>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일을 입력하세요"
                />
              </FormGroup>
            </>
          )}

          <FormGroup>
            <FieldLabelRow>
              <Label>비밀번호</Label>
              {!isRegister && (
                <InlineLink onClick={() => {
                  setIsForgotPassword(true);
                  setError('');
                  setSuccess('');
                }}>비밀번호 찾기</InlineLink>
              )}
            </FieldLabelRow>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호를 입력하세요"
              required
            />
          </FormGroup>

          {isRegister && (
            <FormGroup>
              <FieldLabelRow><Label>비밀번호 확인</Label></FieldLabelRow>
              <Input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="비밀번호를 다시 입력하세요"
                required
              />
            </FormGroup>
          )}

          <PrimaryButton type="submit" disabled={isLoading}>
            {isLoading ? '처리 중...' : (isRegister ? '회원가입' : '로그인')}
          </PrimaryButton>
        </Form>

        <FooterLine>
          {isRegister ? (
            <>
              이미 계정이 있으신가요?
              <span onClick={() => {
                setIsRegister(false);
                setError('');
                setSuccess('');
              }}>로그인</span>
            </>
          ) : (
            <>
              아직 회원이 아니신가요?
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  const parentOrigin = window.location.origin;
                  const url = `https://talk.antsnest.co.kr/popup/register?parentOrigin=${encodeURIComponent(parentOrigin)}`;
                  const popup = window.open(url, 'antsTalkRegister', 'width=480,height=720,resizable=yes,scrollbars=yes');
                  const messageHandler = (event) => {
                    if (!['https://talk.antsnest.co.kr', 'http://localhost:3000'].includes(event.origin)) return;
                    if (event.data?.type === 'SSO_REGISTER_SUCCESS' || event.data?.type === 'SSO_LOGIN_SUCCESS') {
                      window.removeEventListener('message', messageHandler);
                      try { popup && popup.close(); } catch (_) {}
                      window.location.reload();
                    }
                  };
                  window.addEventListener('message', messageHandler);
                }}
              >회원가입</a>
            </>
          )}
        </FooterLine>
      </>
    );
  };

  return (
    <Page>
      <Card>
        <BrandingPanel>
          <BrandIllustration src={Illustration} alt="견적서 시스템" />
        </BrandingPanel>

        <FormPanel>
          {renderRightForm()}
        </FormPanel>
      </Card>
      <Footer>© 2026 ANTSNEST. All rights reserved.</Footer>
    </Page>
  );
};

export default Login;
